import { useRef, useMemo } from 'react';

interface LongPressOptions {
    threshold?: number;
    onStart?: (event: MouseEvent | TouchEvent) => void;
    onFinish?: (event: MouseEvent | TouchEvent) => void;
    onCancel?: (event: MouseEvent | TouchEvent) => void;
}

interface Handlers {
    onMouseDown: any;
    onMouseUp: any;
    onMouseLeave: any;
    onTouchStart: any;
    onTouchEnd: any;
}

const useLongPress = (
    callback: (event: MouseEvent | TouchEvent) => void,
    options: LongPressOptions = {},
): Handlers => {
    const { threshold = 500, onStart, onFinish, onCancel } = options;
    const isLongPressActive = useRef(false);
    const isPressed = useRef(false);
    const timerId = useRef<number | undefined>();

    return useMemo(() => {
        if (typeof callback !== 'function') {
            return {} as Handlers;
        }

        const start = (event: MouseEvent | TouchEvent) => {
            if (!isMouseEvent(event) && !isTouchEvent(event)) return;

            if (onStart) {
                onStart(event);
            }

            isPressed.current = true;
            timerId.current = window.setTimeout(() => {
                callback(event);
                isLongPressActive.current = true;
            }, threshold);
        };

        const cancel = (event: MouseEvent | TouchEvent) => {
            if (!isMouseEvent(event) && !isTouchEvent(event)) return;

            if (isLongPressActive.current) {
                if (onFinish) {
                    onFinish(event);
                }
            } else if (isPressed.current) {
                if (onCancel) {
                    onCancel(event);
                }
            }

            isLongPressActive.current = false;
            isPressed.current = false;

            if (timerId.current) {
                window.clearTimeout(timerId.current);
            }
        };

        const mouseHandlers: Handlers = {
            onMouseDown: start,
            onMouseUp: cancel,
            onMouseLeave: cancel,
            onTouchStart: start,
            onTouchEnd: cancel,
        };

        return mouseHandlers;
    }, [callback, threshold, onCancel, onFinish, onStart]);
};

function extractNativeEvent(event: any): Event {
    return event.nativeEvent || event;
}

function isMouseEvent(event: any): event is MouseEvent {
    const nativeEvent = extractNativeEvent(event);
    return nativeEvent instanceof MouseEvent;
}

function isTouchEvent(event: any): event is TouchEvent {
    const nativeEvent = extractNativeEvent(event);
    return nativeEvent instanceof TouchEvent;
}

export default useLongPress;
